import React, { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { filter } from 'lodash';
import { Form, FormLabel, Row } from 'react-bootstrap';
import { PaymentOption } from '../../../../../../models/form/paymentOption';
import { selectedFieldSelector, updateSelectedField } from '../../../../../../slices/formadmin';
import CurrencyInput from '../../../../../../components/library/inputs/currency/CurrencyInput';
import { FormattedMessage, useIntl } from 'react-intl';
import { PaymentOptionListModel } from '../../../../../../models/form/fields/paymentOptionList';
import { FieldModel } from '../../../../../../models/form/field';
import { FieldType } from '../../../../../../models/form/fieldType';
import { PaymentChoiceListModel } from '../../../../../../models/form/fields/paymentChoiceList';
import CultureTextInput from '../../../../../../components/culture/CultureTextInput';
import { RootState } from '../../../../../../store';

const PaymentOptionListOptions: FC = () => {
  const fieldTemp = useSelector(selectedFieldSelector) as FieldModel;
  const field =
    fieldTemp.type == FieldType.PaymentOptionList
      ? (useSelector(selectedFieldSelector) as PaymentOptionListModel)
      : (useSelector(selectedFieldSelector) as PaymentChoiceListModel);
  const dispatch = useDispatch();
  const intl = useIntl();
  const optionsProperty = field.type == FieldType.PaymentOptionList ? 'options' : 'choices';
  const selectedLanguage = useSelector((state: RootState) => state.formAdmin.selectedLanguage) as string;
  const localizations = useSelector((state: RootState) => state.formAdmin.form?.localizations) as string[];

  let options: PaymentOption[] = [];
  if (field[optionsProperty]) {
    options = field[optionsProperty];
  }

  const updateOption = (option, index) => {
    const newOptions = [...options.slice(0, index), option, ...options.slice(index + 1)];

    dispatch(
      updateSelectedField({
        ...field,
        [optionsProperty]: newOptions,
      }),
    );
  };

  const handleGoUp = (e: React.MouseEvent<Element, MouseEvent>, idx: number) => {
    if (idx > 0) {
      const tempOptions = [...options];
      const tempObj = tempOptions[idx - 1];
      tempOptions[idx - 1] = tempOptions[idx];
      tempOptions[idx] = tempObj;
      dispatch(
        updateSelectedField({
          ...field,
          [optionsProperty]: tempOptions,
        }),
      );
      e.preventDefault();
    }
  };
  const handleGoDown = (e: React.MouseEvent<Element, MouseEvent>, idx: number) => {
    if (idx < options.length - 1) {
      const tempOptions = [...options];
      const tempObj = tempOptions[idx + 1];
      tempOptions[idx + 1] = tempOptions[idx];
      tempOptions[idx] = tempObj;
      dispatch(
        updateSelectedField({
          ...field,
          [optionsProperty]: tempOptions,
        }),
      );
      e.preventDefault();
    }
  };

  return (
    <Form>
      <Row className="d-flex justify-content-center align-items-center">
        <table className="w-100 mb-4">
          {options?.length > 0 && (
            <thead>
              <tr>
                <th>
                  <FormLabel>
                    <FormattedMessage id="adminProperties.settings.options.order" />
                  </FormLabel>
                </th>
                <th>
                  <FormLabel>
                    <FormattedMessage id="adminProperties.settings.options.options" />
                  </FormLabel>
                </th>
                <th></th>
              </tr>
            </thead>
          )}
          <tbody>
            {options?.map((option, index) => {
              return (
                <tr className="option-row" key={option.value}>
                  <td className="sm-cell">
                    <button
                      className="btn btn-primary move-up"
                      onClick={(e: React.MouseEvent<Element, MouseEvent>) => handleGoUp(e, index)}
                    >
                      <span className="mdi mdi-arrow-up" />
                    </button>
                    <button
                      className=" btn btn-primary move-down"
                      onClick={(e: React.MouseEvent<Element, MouseEvent>) => handleGoDown(e, index)}
                    >
                      <span className="mdi mdi-arrow-down" />
                    </button>
                  </td>
                  <td className="lg-cell pb-2">
                    <div className="d-flex flex-column">
                      <div className="w-100 d-flex flex-row mb-2">
                        <div className="w-50 me-2">
                          <CultureTextInput
                            id={`field.${field.id}.option.${index}.name`}
                            culture={selectedLanguage}
                            localizations={localizations}
                            placeholder={'Name'}
                            value={option.text}
                            onChange={(e) => updateOption({ ...option, text: e }, index)}
                          ></CultureTextInput>
                        </div>
                        <div className="w-50">
                          <CurrencyInput
                            placeholder={'Price'}
                            value={(option.price / 100).toFixed(2)}
                            onBlur={(e) => {
                              updateOption({ ...option, price: Number(e.target.value) * 100 }, index);
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <CultureTextInput
                          id={`field.${field.id}.option.${index}.description`}
                          culture={selectedLanguage}
                          localizations={localizations}
                          placeholder={'Description'}
                          value={option.description}
                          onChange={(e) => updateOption({ ...option, description: e }, index)}
                        ></CultureTextInput>
                      </div>
                    </div>
                  </td>
                  <td className="sm-cell pb-2">
                    <div
                      className="delete-btn ms-2"
                      onClick={() => {
                        const newOptions = filter(options, (item) => item !== option);
                        dispatch(updateSelectedField({ ...field, [optionsProperty]: newOptions }));
                      }}
                    >
                      <span className="mdi mdi-close"></span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <button
          className="w-50 btn btn-primary btn-block btn-lg"
          onClick={(e) => {
            const newOption = new PaymentOption(localizations);
            const newOptions = [...options, { ...newOption }];
            dispatch(updateSelectedField({ ...field, [optionsProperty]: newOptions }));
            e.preventDefault();
          }}
        >
          {intl.formatMessage({ id: 'formAdmin.addButton' })}
        </button>
      </Row>
    </Form>
  );
};

export default PaymentOptionListOptions;
